import { Box, Button, Card, H4, defaultTheme } from "@adfenix/alexandria";
import CollapsibleCard from "components/CollapsibleCard";
import { ValidateBundleFormButton } from "./ValidateBundleFormButton";
import { BundleContentSelector } from "./BundleContentSelector";
import { useNestedFormData } from "../useNestedFormData";
import { MetaTemplate } from "./meta/MetaTemplate";
import { useState } from "react";
import { DeltaTemplate } from "./delta/DeltaTemplate";
import { CampaignTemplateContext } from "./util/CampaignTemplateContext";
import { Dispatch, SetStateAction } from "react";
import { useFormContext } from "react-hook-form";
import { ProgramService } from "@adfenix/types";
import styled from "styled-components";
import { CustomTemplate } from "./custom/CustomTemplate";
import { NotificationList } from "./custom/NotificationList";
import { IconButton } from "components/IconButton/IconButton";
import { SRegularTrashCan } from "@adfenix/icons";
import { useTranslation } from "react-i18next";

interface Props {
  /**
   * The currently selected bundlegroup.
   * This is required to hook up nested form inputs to the main form.
   */
  bundleGroupIndex: number;

  /**
   * The currently selected bundle inside the bundlegroup.
   * This is required to hook up nested form inputs to the main form.
   */
  bundleIndex: number;

  /**
   * Callback function to close the modal.
   */
  setShowModal: Dispatch<SetStateAction<boolean>>;
  onCancel: () => void;
  isNew: boolean;
}

const HeadingBox = styled(Box)`
  display: flex;
  flex-grow: 2;
  justify-content: space-between;
  align-items: center;
`;

export const Campaign = ({
  bundleGroupIndex,
  bundleIndex,
  setShowModal,
  onCancel,
  isNew
}: Props) => {
  const { t } = useTranslation();

  const {
    bundle: { metaTemplates, deltaTemplates, customTemplates, emails, tickets },
    bundleKey
  } = useNestedFormData({
    bundleGroupIndex,
    bundleIndex
  });

  const { setValue } = useFormContext<ProgramService.ProgramModel>();
  const metaTemplatesArray = metaTemplates ?? [];
  const deltaTemplatesArray = deltaTemplates ?? [];
  const customTemplatesArray = customTemplates ?? [];
  const emailsOrDefault = emails ?? [];
  const ticketsOrDefault = tickets ?? [];

  const [openSelector, setOpenSelector] = useState(false);

  const noSelectedTemplates =
    metaTemplatesArray.length === 0 &&
    deltaTemplatesArray.length === 0 &&
    customTemplatesArray.length === 0 &&
    emailsOrDefault.length === 0 &&
    ticketsOrDefault.length === 0;

  return (
    <>
      {metaTemplatesArray.map((campaignTemplate, i) => (
        <CampaignTemplateContext.Provider
          key={campaignTemplate.id ?? i}
          value={{
            bundleGroupIndex,
            bundleIndex,
            templateIndex: i,
            templateType: "meta",
            campaignKey: `${bundleKey}.metaTemplates.${i}`,
            bundleKey,
            campaignTemplate
          }}
        >
          <CollapsibleCard
            p="gutter"
            expandIconColor={defaultTheme.colors.grey800}
            title={
              <HeadingBox>
                <H4>
                  {t("programBuilder.program.template.meta.header")} -{" "}
                  {campaignTemplate.name}
                </H4>
                <IconButton
                  onClick={e => {
                    e.stopPropagation();
                    if (
                      confirm(
                        t("programBuilder.program.template.delete.confirmation")
                      )
                    ) {
                      setValue(
                        `${bundleKey}.metaTemplates`,
                        metaTemplatesArray.filter((_, index) => index !== i)
                      );
                    }
                  }}
                  label="delete-meta-template"
                >
                  <SRegularTrashCan />
                </IconButton>
              </HeadingBox>
            }
            initiallyExpanded={i === 0}
          >
            <MetaTemplate />
          </CollapsibleCard>
        </CampaignTemplateContext.Provider>
      ))}

      {deltaTemplatesArray.map((campaignTemplate, i) => (
        <CampaignTemplateContext.Provider
          key={campaignTemplate.id ?? i}
          value={{
            bundleGroupIndex,
            bundleIndex,
            templateIndex: i,
            templateType: "delta",
            campaignKey: `${bundleKey}.deltaTemplates.${i}`,
            bundleKey,
            campaignTemplate
          }}
        >
          <CollapsibleCard
            p="gutter"
            expandIconColor={defaultTheme.colors.grey800}
            title={
              <HeadingBox>
                <H4>
                  {t("programBuilder.program.template.delta.header")} -{" "}
                  {campaignTemplate.name}
                </H4>
                <IconButton
                  label="delete-delta-template"
                  onClick={e => {
                    e.stopPropagation();
                    if (
                      confirm(
                        t("programBuilder.program.template.delete.confirmation")
                      )
                    ) {
                      setValue(
                        `${bundleKey}.deltaTemplates`,
                        deltaTemplatesArray.filter((_, index) => index !== i)
                      );
                    }
                  }}
                >
                  <SRegularTrashCan />
                </IconButton>
              </HeadingBox>
            }
            initiallyExpanded={i === 0}
          >
            <DeltaTemplate />
          </CollapsibleCard>
        </CampaignTemplateContext.Provider>
      ))}

      {customTemplatesArray.map((campaignTemplate, i) => (
        <CampaignTemplateContext.Provider
          key={campaignTemplate.id ?? i}
          value={{
            bundleGroupIndex,
            bundleIndex,
            templateIndex: i,
            templateType: "custom",
            campaignKey: `${bundleKey}.customTemplates.${i}`,
            bundleKey,
            campaignTemplate
          }}
        >
          <CollapsibleCard
            p="gutter"
            expandIconColor={defaultTheme.colors.grey800}
            title={
              <HeadingBox>
                <H4>
                  {t("programBuilder.program.template.custom.header")} -{" "}
                  {campaignTemplate.name}
                </H4>
                <IconButton
                  label="delete-custom-template"
                  onClick={e => {
                    e.stopPropagation();
                    if (
                      confirm(
                        t("programBuilder.program.template.delete.confirmation")
                      )
                    ) {
                      setValue(
                        `${bundleKey}.customTemplates`,
                        customTemplatesArray.filter((_, index) => index !== i)
                      );
                    }
                  }}
                >
                  <SRegularTrashCan />
                </IconButton>
              </HeadingBox>
            }
            initiallyExpanded={i === 0}
          >
            <CustomTemplate />
          </CollapsibleCard>
        </CampaignTemplateContext.Provider>
      ))}

      <NotificationList
        baseKey={bundleKey}
        emails={emailsOrDefault}
        tickets={ticketsOrDefault}
        wrapCard
      />

      {(noSelectedTemplates || openSelector) && (
        <Card style={{ padding: 0 }}>
          <BundleContentSelector
            bundleGroupIndex={bundleGroupIndex}
            bundleIndex={bundleIndex}
            onSelect={() => setOpenSelector(false)}
          />
        </Card>
      )}

      {!noSelectedTemplates && !openSelector && (
        <Button
          logLabel="add-another-template-button"
          variant="secondary"
          onClick={() => setOpenSelector(true)}
        >
          {t("programBuilder.program.template.addAnother.button")}
        </Button>
      )}

      <ValidateBundleFormButton
        bundleGroupIndex={bundleGroupIndex}
        bundleIndex={bundleIndex}
        setShowModal={setShowModal}
        onCancel={onCancel}
        isNew={isNew}
      />
    </>
  );
};
